// useAppReadiness — per-app data-readiness probe at a scope node. Extracted so the "+ Add" door and the
// App Launcher share ONE honest probe (the launcher still has its own inline copy pending a dedupe —
// AppLauncher.tsx:45-70). Each app's manifest `dataReadiness.probe` (or the generic `/sources` fallback)
// is fetched WALLED at the node: a non-empty array = "ready" (data loaded), empty = "needs" (empty), a
// probe error = "unknown" → the badge is DROPPED (an unknown state is never faked as ready/empty).
//
// NOTE: the probe only distinguishes non-empty vs empty. A "partial" state (some-but-incomplete data)
// is NOT derivable here and needs a richer backend readiness signal — deliberately not synthesised.
import { useEffect, useState } from "react";
import type { SubAppManifest } from "@perlab/registry";
import { getJson, postJson } from "../pages/scoped/useScopedFetch";

// MB5 §6: `partial` = some-but-not-all required inputs accessible (only surfaced when the manifest
// allows partial). `pending` = in flight. A probe/readiness ERROR drops the badge (never faked).
export type Readiness = "ready" | "partial" | "needs" | "pending";

// The typed backend states from POST /app-consumption/manifest-readiness (MB5 M3).
type ManifestReadinessState = "ready" | "partially_ready" | "not_ready" | "stale" | "unsupported" | "sealed_or_missing";

/** Manifest-driven governed readiness for ONE app (MB5 §6) — the SINGLE readiness path shared by
 * useAppReadiness + the App Launcher (no duplicate rules, no app-specific if/else). When the app declares
 * an inputContract, readiness is the backend requirement evaluation (accessible governed inputs per type,
 * wall-correct); otherwise the legacy non-empty probe. A denial/sealed scope (404) or any error THROWS —
 * the caller drops the badge (honest unknown). */
export async function appReadinessProbe(app: SubAppManifest, nodeId: string): Promise<Readiness> {
  const ic = app.inputContract;
  if (ic && ((ic.required_inputs?.length ?? 0) > 0 || (ic.optional_inputs?.length ?? 0) > 0)) {
    const r = await postJson<{ state: ManifestReadinessState }>("/api/workspace", "/app-consumption/manifest-readiness", {
      app_id: app.id, scope_node_id: nodeId, readiness_policy: ic.readiness_policy,
      required_inputs: ic.required_inputs, optional_inputs: ic.optional_inputs, allowed_ref_types: ic.allowed_ref_types,
    });
    if (r.state === "ready") return "ready";
    if (r.state === "partially_ready") return "partial";
    return "needs";   // not_ready / stale / unsupported → needs data (the typed reason lives server-side)
  }
  // legacy: no declared governed inputs → the non-empty walled probe (back-compat).
  const probe = app.dataReadiness?.probe ?? "/sources";
  const raw = await getJson<unknown>("/api/workspace", `${probe}?reader_scope_node_id=${encodeURIComponent(nodeId)}`);
  return hasData(raw) ? "ready" : "needs";
}

/** True iff a probe response carries at least one row. Handles both the legacy bare-array shape and the
 * canonical D-10 Page envelope ({items,total_count}). total_count is preferred — it is the wall-correct
 * row count, so a probe hitting a later empty page never mis-reads as "needs" when data exists. */
export function hasData(raw: unknown): boolean {
  if (Array.isArray(raw)) return raw.length > 0;
  if (raw && typeof raw === "object") {
    const o = raw as { total_count?: unknown; items?: unknown };
    if (typeof o.total_count === "number") return o.total_count > 0;
    if (Array.isArray(o.items)) return o.items.length > 0;
  }
  return false;
}

/**
 * @param apps    the apps to probe (usually those openable at the current node)
 * @param nodeId  the scope node to read readiness at
 * @param enabled gate the probe (e.g. only while a menu is open) — fetch-on-open, no always-on cost
 */
export function useAppReadiness(
  apps: SubAppManifest[],
  nodeId: string,
  enabled = true,
): Record<string, Readiness> {
  const [readiness, setReadiness] = useState<Record<string, Readiness>>({});
  const appKey = apps.map((a) => a.id).join(",");
  useEffect(() => {
    if (!enabled || !nodeId) {
      setReadiness({});
      return;
    }
    let cancelled = false;
    setReadiness(Object.fromEntries(apps.map((a) => [a.id, "pending" as Readiness])));
    for (const a of apps) {
      // MB5 §6: the SINGLE shared readiness path — manifest-driven governed readiness when the app
      // declares inputs, else the legacy probe. No app-specific if/else here.
      appReadinessProbe(a, nodeId)
        .then((state) => {
          if (!cancelled) setReadiness((r) => ({ ...r, [a.id]: state }));
        })
        .catch(() => {
          if (cancelled) return;
          // honest unknown (denial/sealed/error) — drop the badge rather than fake a state
          setReadiness((r) => {
            const { [a.id]: _dropped, ...rest } = r;
            return rest;
          });
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeId, appKey, enabled]);
  return readiness;
}
